import { useTheme } from '@mui/material';
import type { ReactNode } from 'react';

import {
  type ResourceColorName,
  type ResourceColorPalette,
  resourcesColors,
} from '../../../theme/tokens/resourcesColors';
import { resourcesIcons } from '../../../theme/tokens/resourcesIcons';

export type ResourcePreviewListItem = {
  id: string | number;
  label: string;
  meta?: string;
  tag?: ReactNode;
};

export type ResourcePreviewListProps = {
  color: ResourceColorName;
  title: string;
  items: ResourcePreviewListItem[];
  maxItems?: number;
  'data-testid'?: string;
};

export function formatOverflowLabel(hiddenCount: number) {
  return hiddenCount === 1 ? '+ 1 autre' : `+ ${hiddenCount} autres`;
}

export function useResourcePreviewList(props: ResourcePreviewListProps) {
  const theme = useTheme();
  const paletteColor = theme.palette[props.color] ?? resourcesColors[props.color];

  const resourceColor: ResourceColorPalette = {
    main: paletteColor?.main ?? resourcesColors[props.color].main,
    light: paletteColor?.light ?? resourcesColors[props.color].light,
    dark: paletteColor?.dark ?? resourcesColors[props.color].dark,
    contrastText: paletteColor?.contrastText ?? resourcesColors[props.color].contrastText,
  };

  const IconComponent = theme.resourceIcons?.[props.color] ?? resourcesIcons[props.color];
  const visibleItems = props.maxItems === undefined ? props.items : props.items.slice(0, props.maxItems);
  const hiddenCount = props.items.length - visibleItems.length;

  return {
    resourceColor,
    IconComponent,
    visibleItems,
    hiddenCount,
    overflowLabel: hiddenCount > 0 ? formatOverflowLabel(hiddenCount) : null,
  };
}
